import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, MapPin, Phone, Send, Star, Calendar, Car, DollarSign, TrendingUp, MessageSquare, Award, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../lib/api";
import CarCard from "../components/CarCard";
import Seo from "../components/Seo";
import { useAuth } from "../context/AuthContext";

const PLAN_BADGES = {
  basic: { label: "Basic dealer", color: "bg-slate-100 text-slate-700" },
  pro: { label: "Pro dealer", color: "bg-cm-primary text-white" },
  premium: { label: "Premium dealer", color: "bg-gradient-to-r from-amber-400 to-amber-500 text-white" },
};

function buildTelegramHref(value) {
  const v = (value || "").trim();
  if (!v) return null;
  if (v.startsWith("http")) return v;
  if (v.startsWith("@")) return `https://t.me/${v.slice(1)}`;
  if (v.startsWith("+") || /^\d/.test(v)) return `https://t.me/+${v.replace(/\D/g, "")}`;
  return `https://t.me/${v}`;
}

function StarRow({ value, size = "w-4 h-4", interactive = false, onChange }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onChange?.(s)}
          className={interactive ? "press-scale" : "cursor-default"}
          aria-label={`${s} star${s > 1 ? "s" : ""}`}
          data-testid={interactive ? `rating-star-${s}` : undefined}
        >
          <Star className={`${size} ${s <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

export default function DealerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [dealer, setDealer] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("listings");
  const [listingFilter, setListingFilter] = useState("all"); // all | sale | rental
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchDealer = async () => {
    try {
      const { data } = await api.get(`/dealers/${id}`);
      setDealer(data);
    } catch {
      setDealer(false); // signal not-found
    }
  };
  const fetchReviews = async () => {
    try {
      const { data } = await api.get(`/dealers/${id}/reviews`);
      setReviews(data);
    } catch (e) {
      console.warn("reviews fetch failed", e);
    }
  };

  useEffect(() => {
    fetchDealer();
    fetchReviews();
    // eslint-disable-next-line
  }, [id]);

  const myReview = useMemo(() => {
    if (!user) return null;
    return reviews.find((r) => r.user_id === user.id) || null;
  }, [reviews, user]);

  useEffect(() => {
    if (myReview) setReviewForm({ rating: myReview.rating, comment: myReview.comment || "" });
  }, [myReview]);

  if (dealer === false) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-slate-500" data-testid="dealer-not-found">Dealer not found.</div>;
  if (!dealer) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-slate-500">Loading…</div>;

  const stats = dealer.stats || {};
  const yearsActive = dealer.created_at ? Math.max(1, Math.floor((Date.now() - new Date(dealer.created_at).getTime()) / (365 * 24 * 60 * 60 * 1000))) : null;
  const planBadge = PLAN_BADGES[dealer.subscription_plan] || PLAN_BADGES.basic;
  const telegramHref = buildTelegramHref(dealer.telegram);
  const filteredListings = (dealer.listings || []).filter((l) => listingFilter === "all" || l.listing_type === listingFilter);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in to leave a review");
      return;
    }
    if (reviewForm.rating < 1) {
      toast.error("Please choose a star rating");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/dealers/${id}/reviews`, { rating: reviewForm.rating, comment: reviewForm.comment.trim() || null });
      toast.success(myReview ? "Review updated" : "Review submitted — thank you!");
      await Promise.all([fetchDealer(), fetchReviews()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteReview = async (rid) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/dealers/${id}/reviews/${rid}`);
      toast.success("Review deleted");
      setReviewForm({ rating: 0, comment: "" });
      await Promise.all([fetchDealer(), fetchReviews()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="dealer-profile-page" className="bg-slate-50 min-h-screen pb-16">
      <Seo
        title={`${dealer.dealer_name} — Car Market`}
        description={dealer.description || `Browse ${stats.total_listings || 0} cars from ${dealer.dealer_name} on Car Market.`}
        image={dealer.logo}
      />

      {/* Hero */}
      <div className="bg-cm-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <div className="w-24 h-24 rounded-2xl bg-white/10 overflow-hidden flex-shrink-0 border border-white/20 shadow-lg">
              {dealer.logo
                ? <img src={dealer.logo} alt={dealer.dealer_name} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center font-display font-extrabold text-3xl">{dealer.dealer_name?.[0]}</div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight" data-testid="dealer-name">{dealer.dealer_name}</h1>
                {dealer.verified_status && (
                  <span className="inline-flex items-center gap-1 bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
                    <ShieldCheck className="w-3 h-3" /> Verified
                  </span>
                )}
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${planBadge.color}`} data-testid="dealer-plan">
                  <Award className="w-3 h-3" /> {planBadge.label}
                </span>
              </div>
              {dealer.description && <p className="text-sm text-slate-300 mt-2 max-w-2xl leading-relaxed">{dealer.description}</p>}

              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-300">
                {dealer.address && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {dealer.address}</span>}
                <button onClick={() => setTab("reviews")} className="inline-flex items-center gap-1.5 hover:text-white" data-testid="dealer-rating-link">
                  <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> {Number(dealer.rating || 0).toFixed(1)} ({dealer.review_count || 0} review{dealer.review_count === 1 ? "" : "s"})
                </button>
                {yearsActive && <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {yearsActive} year{yearsActive > 1 ? "s" : ""} on Car Market</span>}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {dealer.phone && (
                  <a href={`tel:${dealer.phone}`} data-testid="dealer-call" className="px-4 py-2 rounded-xl bg-white text-cm-primary text-sm font-semibold flex items-center gap-2 press-scale hover:bg-slate-100">
                    <Phone className="w-4 h-4" /> Call
                  </a>
                )}
                {telegramHref && (
                  <a href={telegramHref} target="_blank" rel="noreferrer" data-testid="dealer-telegram" className="px-4 py-2 rounded-xl text-white text-sm font-semibold flex items-center gap-2 press-scale" style={{ backgroundColor: "#229ED9" }}>
                    <Send className="w-4 h-4" /> Telegram
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100 overflow-hidden">
          <div className="p-4 text-center" data-testid="stat-total">
            <Car className="w-5 h-5 text-cm-accent mx-auto" />
            <div className="font-display font-extrabold text-cm-primary text-xl mt-1">{stats.total_listings || 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Active listings</div>
          </div>
          <div className="p-4 text-center" data-testid="stat-sale">
            <DollarSign className="w-5 h-5 text-cm-accent mx-auto" />
            <div className="font-display font-extrabold text-cm-primary text-xl mt-1">{stats.for_sale || 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">For sale</div>
          </div>
          <div className="p-4 text-center" data-testid="stat-rent">
            <TrendingUp className="w-5 h-5 text-cm-accent mx-auto" />
            <div className="font-display font-extrabold text-cm-primary text-xl mt-1">{stats.for_rent || 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">For rent</div>
          </div>
          <div className="p-4 text-center" data-testid="stat-avg">
            <DollarSign className="w-5 h-5 text-cm-accent mx-auto" />
            <div className="font-display font-extrabold text-cm-primary text-xl mt-1">{stats.avg_price ? formatPrice(stats.avg_price) : "—"}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Avg sale price</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur border-b border-slate-200 mt-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto">
          {[
            { id: "listings", label: `Listings (${stats.total_listings || 0})` },
            { id: "reviews", label: `Reviews (${dealer.review_count || 0})` },
            { id: "about", label: "About" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`dealer-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? "border-cm-accent text-cm-primary" : "border-transparent text-slate-500 hover:text-cm-primary"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {tab === "listings" && (
          <div data-testid="dealer-listings-tab">
            <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar">
              {[
                { id: "all", label: `All (${dealer.listings?.length || 0})` },
                { id: "sale", label: `For sale (${stats.for_sale || 0})` },
                { id: "rental", label: `For rent (${stats.for_rent || 0})` },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setListingFilter(f.id)}
                  data-testid={`dealer-filter-${f.id}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border ${listingFilter === f.id ? "bg-cm-primary text-white border-cm-primary" : "bg-white text-slate-700 border-slate-200 hover:border-cm-accent"}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {filteredListings.length === 0 ? (
              <p className="text-slate-500 py-10 text-center">No listings to show.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 stagger">
                {filteredListings.map((l) => <CarCard key={l.id} listing={l} />)}
              </div>
            )}
          </div>
        )}

        {tab === "reviews" && (
          <div data-testid="dealer-reviews-tab" className="max-w-3xl">
            {/* Summary card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-5 mb-5">
              <div className="text-center">
                <div className="font-display font-extrabold text-4xl text-cm-primary leading-none">{Number(dealer.rating || 0).toFixed(1)}</div>
                <StarRow value={Math.round(dealer.rating || 0)} />
                <div className="text-xs text-slate-500 mt-1">{dealer.review_count || 0} review{dealer.review_count === 1 ? "" : "s"}</div>
              </div>
              <div className="flex-1 text-sm text-slate-600">
                {dealer.review_count > 0 ? (
                  <p>Customers say <strong className="text-cm-primary">{dealer.dealer_name}</strong> earned an average of <strong>{Number(dealer.rating).toFixed(1)} / 5</strong>.</p>
                ) : (
                  <p>No reviews yet — be the first to leave one!</p>
                )}
              </div>
            </div>

            {/* Write review form */}
            {user && user.dealer_id !== dealer.id && (
              <form onSubmit={submitReview} className="bg-white border border-slate-200 rounded-2xl p-5 mb-5" data-testid="review-form">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display font-bold text-cm-primary">{myReview ? "Update your review" : "Leave a review"}</h3>
                  {myReview && (
                    <button type="button" onClick={() => deleteReview(myReview.id)} data-testid="review-delete-mine" className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1">
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-slate-500">Rating:</span>
                  <StarRow value={reviewForm.rating} size="w-6 h-6" interactive onChange={(r) => setReviewForm((f) => ({ ...f, rating: r }))} />
                </div>
                <textarea
                  data-testid="review-comment"
                  rows={3}
                  placeholder="Share your experience with this dealer (optional)…"
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent outline-none resize-none"
                />
                <div className="mt-3 flex justify-end">
                  <button type="submit" disabled={submitting} data-testid="review-submit" className="px-5 py-2.5 rounded-xl bg-cm-primary hover:bg-cm-accent text-white text-sm font-semibold disabled:opacity-50 press-scale flex items-center gap-2">
                    {myReview ? <><Pencil className="w-4 h-4" /> Update review</> : <><MessageSquare className="w-4 h-4" /> Submit review</>}
                  </button>
                </div>
              </form>
            )}
            {!user && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800 mb-5">
                <Link to={`/login?next=/dealer/${id}`} className="font-semibold hover:underline">Sign in</Link> to leave a review.
              </div>
            )}

            {/* Reviews list */}
            {reviews.length === 0 ? (
              <p className="text-slate-500 py-10 text-center">No reviews yet.</p>
            ) : (
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`review-${r.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-cm-primary text-white flex items-center justify-center font-bold flex-shrink-0">
                        {r.user_photo ? <img src={r.user_photo} alt="" className="w-full h-full rounded-full object-cover" /> : (r.user_name?.[0] || "?")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-cm-primary text-sm truncate">{r.user_name}</span>
                          <StarRow value={r.rating} size="w-3.5 h-3.5" />
                        </div>
                        <div className="text-xs text-slate-500">
                          {new Date(r.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                          {r.updated_at && " · edited"}
                        </div>
                      </div>
                      {(user?.role === "admin" || user?.id === r.user_id) && user?.id !== r.user_id && (
                        <button onClick={() => deleteReview(r.id)} aria-label="Delete review" data-testid={`review-delete-${r.id}`} className="text-slate-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {r.comment && <p className="text-sm text-slate-700 mt-2.5 leading-relaxed whitespace-pre-wrap">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "about" && (
          <div data-testid="dealer-about-tab" className="max-w-3xl bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
            <div>
              <h3 className="font-display font-bold text-cm-primary mb-2">About {dealer.dealer_name}</h3>
              <p className="text-sm text-slate-700 leading-relaxed">{dealer.description || "No description provided."}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
              {dealer.phone && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Phone</div>
                  <a href={`tel:${dealer.phone}`} className="text-sm text-cm-primary font-semibold hover:underline">{dealer.phone}</a>
                </div>
              )}
              {dealer.telegram && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Telegram</div>
                  <a href={telegramHref} target="_blank" rel="noreferrer" className="text-sm text-cm-primary font-semibold hover:underline">{dealer.telegram}</a>
                </div>
              )}
              {dealer.address && (
                <div className="sm:col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Address</div>
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(dealer.address)}`} target="_blank" rel="noreferrer" className="text-sm text-cm-primary font-semibold hover:underline inline-flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> {dealer.address}
                  </a>
                </div>
              )}
              {dealer.created_at && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Member since</div>
                  <div className="text-sm text-slate-700">{new Date(dealer.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long" })}</div>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Plan</div>
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${planBadge.color}`}>
                  <Award className="w-3 h-3" /> {planBadge.label}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
